/**
 * Lyrics: the words of one part, timed for playback, for the big word (or
 * phrase) over the score.
 *
 * Built from the player's note timeline (every note and rest of every part
 * in seconds, repeats unrolled) and OSMD's lyric model. OSMD already joins
 * the syllables of a split word (MusicXML <syllabic> begin / middle / end)
 * into one LyricWord per verse, so a syllable knows its whole word and its
 * place in it.
 *
 * A syllable lasts from its note until the part's next syllable or next
 * rest, so it carries through melismas and ties. A word split by a short
 * breath keeps showing. Verses follow the repeats (the second time through
 * a bar sings verse 2) unless one is picked; a bar without that verse (a
 * refrain written once) sings the verse that is there.
 *
 * Phrases, for the phrase view, end at a rest in the part or at the end of
 * a sentence; a long one is split where the lyric starts a new line (a
 * capital), then evenly, at a comma where it can be.
 */

// Same moment, as far as the timeline goes (seconds)
const SAME_TIME = 0.0005;
// A rest shorter than this inside a word does not hide it (seconds)
const WORD_GAP = 1.0;
// Show a word this early so its fade-in has finished when its note starts
const WORD_LEAD = 0.06;
// Show the next phrase this early so it can be read ahead, though never
// before the last one has finished (seconds)
const PHRASE_LEAD = 0.8;
// A phrase longer than this (characters) is split
const MAX_PHRASE_CHARS = 40;

export const verseNumber = (verse) => {
  const n = parseInt(verse, 10);
  return Number.isFinite(n) ? n : Infinity;
};

const sortVerses = (verses) => [...verses].sort((a, b) => verseNumber(a) - verseNumber(b) || String(a).localeCompare(String(b)));

// [{ verse, entry }] on an OSMD voice entry, in verse order, blanks left out
export function lyricVersesOf(voiceEntry) {
  const dict = voiceEntry?.LyricsEntries;
  if (!dict || typeof dict.keys !== 'function') return [];
  return sortVerses(dict.keys())
    .map(verse => ({ verse, entry: dict.getValue(verse) }))
    .filter(({ entry }) => entry && typeof entry.Text === 'string' && entry.Text.trim() !== '');
}

const versesOn = (note) => lyricVersesOf(note?.ParentVoiceEntry);

// The verse wanted. Where it has words elsewhere in the bar but not on this
// note, it is holding a syllable (a melisma): null. Where it has none in the
// bar (a refrain written once, a second ending numbered on its own), the
// last verse before it, else the first there is.
export function pickVerse(verses, wanted, versesInBar) {
  const exact = verses.find(v => verseNumber(v.verse) === wanted);
  if (exact) return exact.entry;
  if (versesInBar && versesInBar.has(wanted)) return null;
  const before = verses.filter(v => verseNumber(v.verse) < wanted);
  return (before.length ? before[before.length - 1] : verses[0]).entry;
}

const barOf = (note) => note?.ParentVoiceEntry?.ParentSourceStaffEntry?.VerticalContainerParent?.ParentMeasure || null;

// Syllable text without a verse number printed into it ("2.Si")
const syllableText = (entry) => (entry.Text || '').trim().replace(/^\d+\.\s*/, '');

/**
 * The lyric verses of each part, read from the loaded sheet:
 * Map(partId string -> verse numbers in order). Parts without lyrics are
 * left out.
 */
export function lyricVersesByPart(sheet) {
  const parts = new Map();
  (sheet?.Instruments || []).forEach((instrument) => {
    const verses = instrument.LyricVersesNumbers || [];
    if (verses.length) parts.set(String(instrument.Id), sortVerses(verses));
  });
  return parts;
}

/**
 * Whose words to show: my part when it has lyrics, else the first part
 * that does (a melody line carrying the words for everyone).
 */
export function chooseLyricPart(versesByPart, selectedPart, partControls) {
  if (!versesByPart || versesByPart.size === 0) return null;
  if (selectedPart !== null && selectedPart !== undefined && versesByPart.has(String(selectedPart))) {
    return String(selectedPart);
  }
  const first = (partControls || []).find(p => versesByPart.has(String(p.id)));
  return first ? String(first.id) : versesByPart.keys().next().value;
}

// How many times playback has reached the bar of each repeat-unrolled
// event: { playbackIndex -> pass }. Events carry their bar (originalMeasure)
// and its place in the playback order (playbackIndex) once repeats unroll.
export function passByPlaybackIndex(events) {
  const barAt = new Map();
  events.forEach((e) => {
    if (e.playbackIndex !== undefined && !barAt.has(e.playbackIndex)) barAt.set(e.playbackIndex, e.originalMeasure);
  });
  const reached = new Map();
  const passes = new Map();
  [...barAt.keys()].sort((a, b) => a - b).forEach((index) => {
    const bar = barAt.get(index);
    const pass = (reached.get(bar) || 0) + 1;
    reached.set(bar, pass);
    passes.set(index, pass);
  });
  return passes;
}

// A capital right after a lower-case letter starts a new word, even where
// the file joins them ("Ah-Be-cause", a hyphen drawn across a melisma)
const restartsWord = (before, syllable) => /\p{Ll}[^\p{L}]*$/u.test(before) && /^[^\p{L}]*\p{Lu}/u.test(syllable);

// The written word around syllable `index`: { start, texts, index }
function wordAround(texts, index) {
  let start = index;
  while (start > 0 && !restartsWord(texts[start - 1], texts[start])) start--;
  let end = index + 1;
  while (end < texts.length && !restartsWord(texts[end - 1], texts[end])) end++;
  return { start, texts: texts.slice(start, end), index: index - start };
}

// The part's sounding stretches: overlapping notes merged, rests between
function soundingStretches(notes) {
  const stretches = [];
  [...notes].sort((a, b) => a.startTime - b.startTime).forEach((n) => {
    const last = stretches[stretches.length - 1];
    if (last && n.startTime <= last.end + SAME_TIME) last.end = Math.max(last.end, n.endTime);
    else stretches.push({ start: n.startTime, end: n.endTime });
  });
  return stretches;
}

/**
 * Timed syllables of one part:
 * [{ start, end, wordId, syllables: [text], index }], sorted and
 * not overlapping. `verse` is a verse number, or 'auto' to follow the
 * repeats. `voice` 'bottom' prefers the lower voice where both voices of a
 * shared staff carry words.
 */
export function buildLyricTimeline(events, { partId, verse = 'auto', voice = 'top' }) {
  if (!Array.isArray(events) || partId === null || partId === undefined) return [];
  const wanted = String(partId);
  const notes = events.filter(e => String(e.partId) === wanted && !e.isRest && e.sourceNote);
  if (notes.length === 0) return [];
  const sorted = [...notes].sort((a, b) => a.startTime - b.startTime || (a.voiceId || 0) - (b.voiceId || 0));
  const versesOf = new Map(sorted.map(n => [n, versesOn(n.sourceNote)]));
  const versesByBar = new Map();
  versesOf.forEach((verses, n) => {
    const bar = barOf(n.sourceNote);
    if (!bar) return;
    if (!versesByBar.has(bar)) versesByBar.set(bar, new Set());
    verses.forEach(v => versesByBar.get(bar).add(verseNumber(v.verse)));
  });
  // Following the repeats: pass 3 of a two-verse song sings verse 2
  const passes = verse === 'auto' ? passByPlaybackIndex(events) : null;
  const lastVerse = Math.max(1, ...[...versesOf.values()].flatMap(vs => vs.map(v => verseNumber(v.verse))).filter(Number.isFinite));
  const fixedVerse = verse === 'auto' ? null : verseNumber(verse);

  // One syllable per moment: chords share a voice entry, and where two
  // voices both have words the singer's voice wins
  const onsets = [];
  sorted.forEach((n) => {
    const verses = versesOf.get(n);
    if (verses.length === 0) return;
    const wantedVerse = fixedVerse ?? Math.min(passes?.get(n.playbackIndex) || 1, lastVerse);
    const entry = pickVerse(verses, wantedVerse, versesByBar.get(barOf(n.sourceNote)));
    if (!entry) return;
    const onset = { time: n.startTime, voiceId: n.voiceId || 0, entry };
    const last = onsets[onsets.length - 1];
    if (last && Math.abs(last.time - onset.time) < SAME_TIME) {
      if (voice === 'bottom' ? onset.voiceId > last.voiceId : onset.voiceId < last.voiceId) onsets[onsets.length - 1] = onset;
      return;
    }
    onsets.push(onset);
  });

  const stretches = soundingStretches(notes);
  const spans = [];
  let stretchIndex = 0;
  let wordId = 0;
  let previous = null;
  onsets.forEach((onset, i) => {
    while (stretchIndex < stretches.length - 1 && stretches[stretchIndex].end <= onset.time + SAME_TIME) stretchIndex++;
    const next = onsets[i + 1];
    const end = Math.min(stretches[stretchIndex].end, next ? next.time : Infinity);
    const word = onset.entry.Word;
    const entries = word ? word.Syllables : [onset.entry];
    const index = Math.max(0, entries.indexOf(onset.entry));
    const shown = wordAround(entries.map(syllableText), index);
    // A new word, or the same word starting over (a repeat)
    const sameWord = previous && word && previous.word === word && previous.start === shown.start && index > previous.index;
    if (!sameWord) wordId++;
    else if (onset.time - previous.span.end < WORD_GAP) previous.span.end = onset.time;
    const span = {
      start: onset.time,
      end: Math.max(end, onset.time + SAME_TIME),
      wordId,
      syllables: shown.texts,
      index: shown.index
    };
    spans.push(span);
    previous = { word, index, start: shown.start, span };
  });
  return spans;
}

const wordText = (word) => word.syllables.join('');
const lastSyllable = (word) => word.syllables[word.syllables.length - 1] || '';
const endsSentence = (text) => /[.!?;:…]['"’”»)\]]*$/.test(text);
const endsClause = (text) => /[,–—]['"’”»)\]]*$/.test(text);
const phraseChars = (words) => words.reduce((n, w) => n + wordText(w).length, 0) + Math.max(0, words.length - 1);

// A capital after a lower-case word starts a new line of the lyric ("I",
// "I'm" and the like aside)
const startsLine = (previous, word) => /\p{Ll}[^\p{L}]*$/u.test(lastSyllable(previous))
  && /^[^\p{L}]*\p{Lu}/u.test(word.syllables[0] || '')
  && !/^I(['’]\p{L}+)?$/u.test(wordText(word));

// Words that belong with the next one, so never end a cut-up line
const LEANS_ON_NEXT = /^(I|a|an|the|to|of|and|or|my|your|our|their|in|on|at|by|for|with|from)$/i;
const leansOnNext = (word) => LEANS_ON_NEXT.test(wordText(word).replace(/[^\p{L}]/gu, ''));

// A run too long for one phrase, in even pieces, cut after a comma where
// one is close enough
function evenPieces(run) {
  const total = phraseChars(run);
  if (total <= MAX_PHRASE_CHARS) return [run];
  const target = total / Math.ceil(total / MAX_PHRASE_CHARS);
  const limit = Math.min(MAX_PHRASE_CHARS, target * 1.15);
  const pieces = [[]];
  let chars = 0;
  run.forEach((word, i) => {
    const piece = pieces[pieces.length - 1];
    piece.push(word);
    chars += (piece.length > 1 ? 1 : 0) + wordText(word).length;
    const next = run[i + 1];
    // The rest of the run fits with this piece: no cut, so no stray last word
    if (!next || chars + 1 + phraseChars(run.slice(i + 1)) <= MAX_PHRASE_CHARS) return;
    const atComma = endsClause(lastSyllable(word)) && chars >= target * 0.6;
    // Run a little long rather than start a line with the end of a clause
    const commaNext = endsClause(lastSyllable(next)) && chars + 1 + wordText(next).length <= MAX_PHRASE_CHARS;
    if (atComma) {
      pieces.push([]);
      chars = 0;
    } else if (!commaNext && chars + 1 + wordText(next).length > limit) {
      const carried = piece.length > 1 && leansOnNext(word) ? [piece.pop()] : [];
      pieces.push(carried);
      chars = carried.length ? wordText(word).length : 0;
    }
  });
  return pieces;
}

// A run too long for one phrase: at the lyric's own line starts first, then
// any line still too long in even pieces
function splitRun(run) {
  if (phraseChars(run) <= MAX_PHRASE_CHARS) return [run];
  const lines = [[]];
  run.forEach((word, i) => {
    if (i > 0 && startsLine(run[i - 1], word)) {
      const line = lines[lines.length - 1];
      lines.push(line.length > 1 && leansOnNext(line[line.length - 1]) ? [line.pop()] : []);
    }
    lines[lines.length - 1].push(word);
  });
  return lines.flatMap(evenPieces);
}

/**
 * The timeline's words in phrases:
 * [{ id, showFrom, start, end, words: [{ wordId, syllables }] }]
 */
export function buildLyricPhrases(spans) {
  const words = [];
  (spans || []).forEach((span) => {
    const last = words[words.length - 1];
    if (last && last.wordId === span.wordId) last.end = span.end;
    else words.push({ wordId: span.wordId, syllables: span.syllables, start: span.start, end: span.end });
  });
  const runs = [];
  words.forEach((word, i) => {
    const previous = words[i - 1];
    if (!previous || word.start - previous.end > SAME_TIME || endsSentence(lastSyllable(previous))) runs.push([]);
    runs[runs.length - 1].push(word);
  });
  const phrases = runs.flatMap(splitRun).map((ws, i) => ({
    id: i + 1,
    start: ws[0].start,
    end: ws[ws.length - 1].end,
    words: ws.map(({ wordId, syllables }) => ({ wordId, syllables }))
  }));
  phrases.forEach((phrase, i) => {
    phrase.showFrom = Math.max(i > 0 ? phrases[i - 1].end : -Infinity, phrase.start - PHRASE_LEAD);
  });
  return phrases;
}

// The last item whose `from` is at or before `time`, if it is still on
function itemAt(items, time, from, end) {
  if (!items || items.length === 0 || time === null || time === undefined) return null;
  let lo = 0;
  let hi = items.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (from(items[mid]) <= time) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return found >= 0 && time < end(items[found]) ? items[found] : null;
}

/**
 * The syllable sounding at `time`, or about to within `lead` seconds;
 * null in a rest.
 */
export function lyricAt(spans, time, lead = 0) {
  return itemAt(spans, time, s => s.start - lead, s => s.end);
}

/**
 * What to show at `time` in `mode` ('word' or 'phrase'), or null:
 * { key, phrase, sig, words: [{ syllables, now }] } where `now` is the
 * index of the syllable being sung (-1 for other words). `key` changes
 * with the word or phrase (a fade), `sig` with anything shown.
 */
export function lyricDisplayAt({ spans, phrases }, time, mode) {
  const span = lyricAt(spans, time, WORD_LEAD);
  const sung = span ? `${span.wordId}.${span.index}` : '';
  if (mode === 'phrase') {
    const phrase = itemAt(phrases, time, p => Math.min(p.showFrom, p.start - WORD_LEAD), p => p.end);
    if (!phrase) return null;
    return {
      key: `p${phrase.id}`,
      phrase: true,
      sig: `p${phrase.id}|${sung}`,
      words: phrase.words.map(w => ({ syllables: w.syllables, now: span && span.wordId === w.wordId ? span.index : -1 }))
    };
  }
  if (!span) return null;
  return { key: `w${span.wordId}`, phrase: false, sig: `w${sung}`, words: [{ syllables: span.syllables, now: span.index }] };
}
